import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';
const BOOKINGS_API = 'https://api.botanix.in/api/bookings';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const CANCELLATION_POLICIES = [
  { id:'flexible', label:'Flexible — Full Refund', charge_pct:0 },
  { id:'moderate', label:'Moderate — 50% Refund', charge_pct:50 },
  { id:'strict', label:'Strict — No Refund', charge_pct:100 },
  { id:'custom', label:'Custom Amount', charge_pct:null },
];

const REFUND_MODES = ['Bank Transfer','UPI','Cash','Cheque','Credit Card Reversal','Wallet'];

const inp = {
  width:'100%', padding:'9px 12px', background:'#F3F6F3',
  border:'1.5px solid #CDD8CD', borderRadius:8,
  color:'#1A2E1E', fontSize:13, boxSizing:'border-box',
};
const lbl = (t) => <div style={{fontSize:11,fontWeight:700,color:'#7A9A80',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>{t}</div>;

const StatusBadge = ({status}) => {
  const colors = {
    Pending: {bg:C.amberBg, color:C.amber},
    Approved: {bg:C.blueBg, color:C.blue},
    Processed: {bg:C.accentLight, color:C.accent},
    Rejected: {bg:C.redBg, color:C.red},
  };
  const s = colors[status] || colors.Pending;
  return <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,...s}}>{status}</span>;
};

export default function RefundManagement() {
  const [refunds, setRefunds] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewRefund, setViewRefund] = useState(null);

  // New refund form
  const [step, setStep] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingSearch, setBookingSearch] = useState('');
  const [policy, setPolicy] = useState('flexible');
  const [customCharge, setCustomCharge] = useState(0);
  const [refundMode, setRefundMode] = useState('Bank Transfer');
  const [bankName, setBankName] = useState('');
  const [accountNum, setAccountNum] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [upiId, setUpiId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [notes, setNotes] = useState('');

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('botanix_user')); } catch { return null; } })();

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, bRes] = await Promise.all([
        fetch(`${API}/refunds`).then(r=>r.json()),
        fetch(`${BOOKINGS_API}`).then(r=>r.json()),
      ]);
      setRefunds(Array.isArray(rRes)?rRes:[]);
      setBookings(Array.isArray(bRes)?bRes.filter(b=>b.booking_status!=='Cancelled'&&parseFloat(b.advance_paid||0)>0):[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(()=>{ load(); },[load]);

  const getChargeAmount = () => {
    if (!selectedBooking) return 0;
    const advance = parseFloat(selectedBooking.advance_paid||0);
    if (policy === 'custom') return parseFloat(customCharge||0);
    const pol = CANCELLATION_POLICIES.find(p=>p.id===policy);
    return Math.round(advance * (pol?.charge_pct||0) / 100);
  };

  const getRefundAmount = () => {
    if (!selectedBooking) return 0;
    const advance = parseFloat(selectedBooking.advance_paid||0);
    return Math.max(0, advance - getChargeAmount());
  };

  const submitRefund = async () => {
    if (!selectedBooking) return;
    setSaving(true);
    try {
      const pol = CANCELLATION_POLICIES.find(p=>p.id===policy);
      const r = await fetch(`${API}/refunds`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          booking_id: selectedBooking.id,
          booking_code: selectedBooking.booking_code,
          guest_name: selectedBooking.guest_name,
          guest_phone: selectedBooking.guest_phone,
          original_amount: selectedBooking.total_amount,
          advance_paid: selectedBooking.advance_paid,
          cancellation_charge: getChargeAmount(),
          refund_amount: getRefundAmount(),
          refund_mode: refundMode,
          bank_name: bankName||null,
          account_number: accountNum||null,
          ifsc_code: ifsc||null,
          upi_id: upiId||null,
          cancellation_reason: cancelReason,
          cancellation_policy: pol?.label || policy,
          requested_by: currentUser?.name || 'Staff',
          notes,
        })
      });
      if (r.ok) {
        setShowNew(false); setStep(1); setSelectedBooking(null);
        load(); showMsg('Refund request created successfully!');
      } else { const d=await r.json(); showMsg(d.error||'Failed'); }
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const approveRefund = async (id) => {
    await fetch(`${API}/refunds/${id}/approve`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ approved_by: currentUser?.name||'Manager' })
    });
    load(); showMsg('Refund approved!');
  };

  const processRefund = async (id) => {
    await fetch(`${API}/refunds/${id}/process`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ processed_by: currentUser?.name||'Accounts' })
    });
    load(); showMsg('Refund marked as processed!');
  };

  const rejectRefund = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    await fetch(`${API}/refunds/${id}/reject`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ notes: reason })
    });
    load(); showMsg('Refund rejected');
  };

  const filteredRefunds = refunds.filter(r => {
    const matchSearch = !search || r.guest_name?.toLowerCase().includes(search.toLowerCase()) || r.booking_code?.toLowerCase().includes(search.toLowerCase()) || r.refund_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const filteredBookings = bookings.filter(b =>
    !bookingSearch || b.booking_code?.toLowerCase().includes(bookingSearch.toLowerCase()) || b.guest_name?.toLowerCase().includes(bookingSearch.toLowerCase())
  );

  // Stats
  const pending = refunds.filter(r=>r.status==='Pending').length;
  const approved = refunds.filter(r=>r.status==='Approved').length;
  const totalRefunded = refunds.filter(r=>r.status==='Processed').reduce((s,r)=>s+parseFloat(r.refund_amount||0),0);

  const NewRefundModal = () => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:620,maxHeight:'90vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>New Refund Request</div>
          <button onClick={()=>{setShowNew(false);setStep(1);setSelectedBooking(null);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Cancel</button>
        </div>

        {/* Step indicators */}
        <div style={{display:'flex',gap:8,marginBottom:24}}>
          {['Select Booking','Cancellation Policy','Refund Details'].map((s,i)=>(
            <div key={s} style={{flex:1,textAlign:'center'}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:step>i+1?C.accent:step===i+1?C.accent:C.border,color:step>=i+1?'#fff':C.text3,fontWeight:700,fontSize:13,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 4px'}}>
                {step>i+1?'✓':i+1}
              </div>
              <div style={{fontSize:10,color:step===i+1?C.accent:C.text3,fontWeight:600}}>{s}</div>
            </div>
          ))}
        </div>

        {/* STEP 1: Select Booking */}
        {step===1 && (
          <div>
            <div style={{marginBottom:14}}>
              {lbl('Search Booking')}
              <input style={inp} value={bookingSearch} onChange={e=>setBookingSearch(e.target.value)} placeholder="Search by booking code or guest name..." />
            </div>
            <div style={{maxHeight:300,overflowY:'auto',border:`1px solid ${C.border}`,borderRadius:10}}>
              {filteredBookings.length===0 ? (
                <div style={{padding:20,textAlign:'center',color:C.text3,fontSize:13}}>No bookings with advance payment found</div>
              ) : filteredBookings.map(b=>(
                <div key={b.id} onClick={()=>setSelectedBooking(b)}
                  style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,cursor:'pointer',
                    background:selectedBooking?.id===b.id?C.accentLight:C.surface,
                    borderLeft:selectedBooking?.id===b.id?`3px solid ${C.accent}`:'3px solid transparent'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:C.text}}>{b.guest_name}</div>
                      <div style={{fontSize:12,color:C.text3}}>{b.booking_code} · {b.booking_type} · {new Date(b.start_date).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:13,color:C.text2}}>Total: {inr(b.total_amount)}</div>
                      <div style={{fontSize:14,fontWeight:700,color:C.accent}}>Advance: {inr(b.advance_paid)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {selectedBooking && (
              <div style={{marginTop:14,padding:'12px 16px',background:C.accentLight,borderRadius:10,border:`1px solid ${C.accent}`}}>
                <div style={{fontSize:13,fontWeight:700,color:C.accent}}>Selected: {selectedBooking.guest_name} — {selectedBooking.booking_code}</div>
                <div style={{fontSize:12,color:C.text2,marginTop:4}}>Advance Paid: {inr(selectedBooking.advance_paid)} · Total: {inr(selectedBooking.total_amount)}</div>
              </div>
            )}
            <button onClick={()=>setStep(2)} disabled={!selectedBooking}
              style={{width:'100%',padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',marginTop:16,opacity:!selectedBooking?0.4:1}}>
              Next →
            </button>
          </div>
        )}

        {/* STEP 2: Cancellation Policy */}
        {step===2 && selectedBooking && (
          <div>
            <div style={{padding:'14px 16px',background:C.bg,borderRadius:10,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{selectedBooking.guest_name} — {selectedBooking.booking_code}</div>
              <div style={{fontSize:12,color:C.text3}}>Advance Paid: {inr(selectedBooking.advance_paid)}</div>
            </div>

            <div style={{marginBottom:16}}>
              {lbl('Cancellation Policy')}
              {CANCELLATION_POLICIES.map(p=>(
                <div key={p.id} onClick={()=>setPolicy(p.id)}
                  style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',
                    border:`2px solid ${policy===p.id?C.accent:C.border}`,borderRadius:10,marginBottom:8,cursor:'pointer',
                    background:policy===p.id?C.accentLight:C.surface}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{p.label}</div>
                    {p.charge_pct!==null && <div style={{fontSize:11,color:C.text3}}>Cancellation charge: {p.charge_pct}% of advance</div>}
                  </div>
                  <div style={{textAlign:'right'}}>
                    {p.charge_pct!==null && (
                      <>
                        <div style={{fontSize:12,color:C.red}}>Charge: {inr(Math.round(parseFloat(selectedBooking.advance_paid||0)*p.charge_pct/100))}</div>
                        <div style={{fontSize:14,fontWeight:700,color:C.accent}}>Refund: {inr(Math.max(0,parseFloat(selectedBooking.advance_paid||0)-Math.round(parseFloat(selectedBooking.advance_paid||0)*p.charge_pct/100)))}</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {policy==='custom' && (
                <div style={{marginTop:10}}>
                  {lbl('Custom Cancellation Charge (Rs)')}
                  <input style={inp} type="number" value={customCharge} onChange={e=>setCustomCharge(e.target.value)} placeholder="Enter charge amount" />
                  <div style={{fontSize:12,color:C.accent,marginTop:6,fontWeight:600}}>
                    Refund Amount: {inr(getRefundAmount())}
                  </div>
                </div>
              )}
            </div>

            <div style={{marginBottom:14}}>
              {lbl('Cancellation Reason')}
              <textarea style={{...inp,height:70,resize:'vertical'}} value={cancelReason} onChange={e=>setCancelReason(e.target.value)} placeholder="Why is the guest cancelling?" />
            </div>

            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setStep(1)} style={{flex:1,padding:'12px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:10,fontSize:14,fontWeight:600,cursor:'pointer'}}>← Back</button>
              <button onClick={()=>setStep(3)}
                style={{flex:2,padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Next → Refund: {inr(getRefundAmount())}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Refund Details */}
        {step===3 && selectedBooking && (
          <div>
            {/* Summary */}
            <div style={{padding:'16px',background:C.bg,borderRadius:12,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:10}}>Refund Summary</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:13}}>
                <div><span style={{color:C.text3}}>Guest: </span><strong style={{color:C.text}}>{selectedBooking.guest_name}</strong></div>
                <div><span style={{color:C.text3}}>Booking: </span><strong style={{color:C.text}}>{selectedBooking.booking_code}</strong></div>
                <div><span style={{color:C.text3}}>Advance Paid: </span><strong style={{color:C.text}}>{inr(selectedBooking.advance_paid)}</strong></div>
                <div><span style={{color:C.text3}}>Cancellation Charge: </span><strong style={{color:C.red}}>{inr(getChargeAmount())}</strong></div>
                <div style={{gridColumn:'span 2',paddingTop:8,borderTop:`1px solid ${C.border}`}}>
                  <span style={{color:C.text3}}>Refund Amount: </span>
                  <strong style={{color:C.accent,fontSize:18}}>{inr(getRefundAmount())}</strong>
                </div>
              </div>
            </div>

            <div style={{marginBottom:14}}>
              {lbl('Refund Mode')}
              <select style={inp} value={refundMode} onChange={e=>setRefundMode(e.target.value)}>
                {REFUND_MODES.map(m=><option key={m}>{m}</option>)}
              </select>
            </div>

            {(refundMode==='Bank Transfer'||refundMode==='Cheque') && (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
                <div>{lbl('Bank Name')}<input style={inp} value={bankName} onChange={e=>setBankName(e.target.value)} placeholder="Bank name" /></div>
                <div>{lbl('Account Number')}<input style={inp} value={accountNum} onChange={e=>setAccountNum(e.target.value)} /></div>
                <div>{lbl('IFSC Code')}<input style={inp} value={ifsc} onChange={e=>setIfsc(e.target.value)} /></div>
              </div>
            )}

            {refundMode==='UPI' && (
              <div style={{marginBottom:14}}>
                {lbl('UPI ID')}
                <input style={inp} value={upiId} onChange={e=>setUpiId(e.target.value)} placeholder="e.g. name@upi" />
              </div>
            )}

            <div style={{marginBottom:14}}>
              {lbl('Additional Notes')}
              <textarea style={{...inp,height:60,resize:'vertical'}} value={notes} onChange={e=>setNotes(e.target.value)} />
            </div>

            <div style={{padding:'12px 14px',background:C.amberBg,border:`1px solid ${C.amberBorder}`,borderRadius:10,marginBottom:16,fontSize:12,color:C.amber}}>
              This will cancel the booking and initiate a refund request. GM approval required before processing.
            </div>

            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setStep(2)} style={{flex:1,padding:'12px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:10,fontSize:14,fontWeight:600,cursor:'pointer'}}>← Back</button>
              <button onClick={submitRefund} disabled={saving}
                style={{flex:2,padding:'12px',background:C.red,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:saving?0.6:1}}>
                {saving?'Submitting...':'Submit Refund Request'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const RefundDetail = ({refund}) => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:560,padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{refund.refund_number}</div>
          <button onClick={()=>setViewRefund(null)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Close</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16,padding:16,background:C.bg,borderRadius:10}}>
          {[
            ['Guest',refund.guest_name],['Booking',refund.booking_code],
            ['Original Amount',inr(refund.original_amount)],['Advance Paid',inr(refund.advance_paid)],
            ['Cancellation Charge',inr(refund.cancellation_charge)],['Refund Amount',inr(refund.refund_amount)],
            ['Refund Mode',refund.refund_mode],['Policy',refund.cancellation_policy],
            ['Requested By',refund.requested_by||'—'],['Status',refund.status],
          ].map(([k,v])=>(
            <div key={k}>
              <div style={{fontSize:11,color:C.text3,marginBottom:2}}>{k}</div>
              <div style={{fontSize:13,fontWeight:700,color:k==='Refund Amount'?C.accent:k==='Cancellation Charge'?C.red:C.text}}>{v}</div>
            </div>
          ))}
        </div>

        {refund.bank_name && (
          <div style={{padding:'12px 14px',background:C.bg,borderRadius:8,marginBottom:12,fontSize:12}}>
            <div style={{fontWeight:700,color:C.text2,marginBottom:6}}>Bank Details</div>
            <div>Bank: {refund.bank_name} | A/C: {refund.account_number} | IFSC: {refund.ifsc_code}</div>
          </div>
        )}
        {refund.upi_id && <div style={{padding:'12px 14px',background:C.bg,borderRadius:8,marginBottom:12,fontSize:12}}>UPI: {refund.upi_id}</div>}
        {refund.cancellation_reason && <div style={{padding:'12px 14px',background:C.redBg,borderRadius:8,marginBottom:12,fontSize:12,color:C.red}}>Reason: {refund.cancellation_reason}</div>}

        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {refund.status==='Pending' && (
            <>
              <button onClick={()=>{approveRefund(refund.id);setViewRefund(null);}}
                style={{flex:1,padding:'10px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Approve</button>
              <button onClick={()=>{rejectRefund(refund.id);setViewRefund(null);}}
                style={{flex:1,padding:'10px',background:C.redBg,color:C.red,border:`1px solid ${C.red}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Reject</button>
            </>
          )}
          {refund.status==='Approved' && (
            <button onClick={()=>{processRefund(refund.id);setViewRefund(null);}}
              style={{flex:1,padding:'10px',background:C.blue,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Mark as Processed</button>
          )}
          <button onClick={()=>window.print()}
            style={{padding:'10px 16px',background:C.bg,color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>Print</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{padding:'24px 28px',background:C.bg,minHeight:'100vh'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>Refund Management</div>
          <div style={{fontSize:13,color:C.text3,marginTop:4}}>{refunds.length} total · {pending} pending · {approved} approved</div>
        </div>
        <button onClick={()=>{setShowNew(true);setStep(1);setSelectedBooking(null);}}
          style={{padding:'9px 18px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
          + New Refund Request
        </button>
      </div>

      {msg && <div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:msg.includes('Error')||msg.includes('Failed')?C.redBg:C.accentLight,color:msg.includes('Error')||msg.includes('Failed')?C.red:C.accent,border:`1px solid ${msg.includes('Error')||msg.includes('Failed')?C.red:C.accent}`}}>{msg}</div>}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          ['Total Requests',refunds.length,C.text,C.surface],
          ['Pending Approval',pending,C.amber,C.amberBg],
          ['Approved',approved,C.blue,C.blueBg],
          ['Total Refunded',inr(totalRefunded),C.accent,C.accentLight],
        ].map(([label,value,color,bg])=>(
          <div key={label} style={{background:bg,border:`1px solid ${C.border}`,borderRadius:12,padding:16,textAlign:'center'}}>
            <div style={{fontSize:22,fontWeight:800,color}}>{value}</div>
            <div style={{fontSize:11,color:C.text3,fontWeight:600,textTransform:'uppercase',marginTop:4}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search guest, booking code..."
          style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13,width:220}} />
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13}}>
          <option value="">All Status</option>
          <option>Pending</option><option>Approved</option><option>Processed</option><option>Rejected</option>
        </select>
        <button onClick={load} style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>Refresh</button>
      </div>

      {/* Refunds List */}
      {loading ? <div style={{textAlign:'center',padding:60,color:C.text3}}>Loading...</div> : (
        filteredRefunds.length===0 ? (
          <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
            No refund requests yet. Click "+ New Refund Request" to initiate one.
          </div>
        ) : (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:C.bg}}>
                  {['Refund #','Guest','Booking','Advance Paid','Charge','Refund Amt','Mode','Status','Actions'].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRefunds.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                    <td style={{padding:'10px 14px',fontFamily:'monospace',fontSize:12,color:C.text2}}>{r.refund_number}</td>
                    <td style={{padding:'10px 14px',fontWeight:700,color:C.text}}>{r.guest_name}</td>
                    <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{r.booking_code}</td>
                    <td style={{padding:'10px 14px',color:C.text2}}>{inr(r.advance_paid)}</td>
                    <td style={{padding:'10px 14px',color:C.red,fontWeight:600}}>{inr(r.cancellation_charge)}</td>
                    <td style={{padding:'10px 14px',fontWeight:800,color:C.accent,fontSize:14}}>{inr(r.refund_amount)}</td>
                    <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{r.refund_mode}</td>
                    <td style={{padding:'10px 14px'}}><StatusBadge status={r.status} /></td>
                    <td style={{padding:'10px 14px'}}>
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>setViewRefund(r)}
                          style={{padding:'4px 10px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>View</button>
                        {r.status==='Pending' && (
                          <button onClick={()=>approveRefund(r.id)}
                            style={{padding:'4px 10px',background:C.blueBg,color:C.blue,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>Approve</button>
                        )}
                        {r.status==='Approved' && (
                          <button onClick={()=>processRefund(r.id)}
                            style={{padding:'4px 10px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>Process</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showNew && <NewRefundModal />}
      {viewRefund && <RefundDetail refund={viewRefund} />}
    </div>
  );
}
